import React, {
    useState,
    createContext,
    useContext,
    useEffect,
    useRef,
    useCallback
} from "react";
import "./App.css";
import { marked } from 'marked';
import DOMPurify from 'dompurify';
import LoadingSpinnerImage from "../TaskManagerApp/LoadingSpinnerImage";
import ComparisonItemSearch from "./ComparisonItemSearch.js";
import RegulatoryReviewsDocumentComparison from '../SystematicReviews/RegulatoryReviewsDocumentComparison.js';

const AIChat = ({ AppContext = null, entityId, entityType, uniqueId, enableTemplateQuestions = false, enableAIOverviewButton = false, enableComparisonButton = false, enableRequirementsButton = false, enableUserChat = true, isClinicalLiterature = false, embeddedAIComponent = false, autoCallOverviewFunction = false, defaultAiTask = "question", multiItemData = [], callbackMultiItemDataSelectPDF = null, systematicReviewType = null, filePath = null, otherDocVersions = false, documentTitle = null, translationMediaPath = null, chatApiUrl = null, multiItemChatApiUrl = null }) => {


    const [sessionId, setSessionId] = useState("");
    const [AITypeWriterIsTyping, setAITypeWriterIsTyping] = useState(false);
    const [chatContent, setChatContent] = useState([]);
    const [userText, setUserText] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [aiChatHistory, setAiChatHistory] = useState([]);
    const [showError, setShowError] = useState(false);
    const [showCustomError, setShowCustomError] = useState(false);
    const [customErrorMessage, setCustomErrorMessage] = useState("");
    const [showAIOverviewButton, setShowAIOverviewButton] = useState(enableAIOverviewButton);
    const [showTemplateQuestions, setShowTemplateQuestions] = useState(enableTemplateQuestions);
    const [showUserChatBox, setShowUserChatBox] = useState(enableUserChat);
    const [isVisibleFeedbackPopup, setIsVisibleFeedbackPopup] = useState(false);
    const [selectedFeedbackId, setSelectedFeedbackId] = useState(null);
    const [feedbackMessage, setFeedbackMessage] = useState("");
    const [finishedFeedback, setFinishedFeedback] = useState(false);
    const [showDisclaimer, setShowDisclaimer] = useState(true);
    const [showComparisonItemSearchPopup, setShowComparisonItemSearchPopup] = useState(false);
    const [showVersionComparisonButton, setShowVersionComparisonButton] = useState(enableComparisonButton);
    const [showGeneralComparisonButton, setShowGeneralComparisonButton] = useState(enableComparisonButton);
    const [showRequirementsButton, setShowRequirementsButton] = useState(enableRequirementsButton);

    const [showVersionComparisonButtonDisabled, setShowVersionComparisonButtonDisabled] = useState(false);
    const [showGeneralComparisonButtonDisabled, setShowGeneralComparisonButtonDisabled] = useState(false);
    const [showRequirementsButtonDisabled, setShowRequirementsButtonDisabled] = useState(false);
    const [isShowingOptionsMenu, setIsShowingOptionsMenu] = useState(false);
    const [optionsMenuPosition, setOptionsMenuPosition] = useState({ top: 0, left: 0 });
    const [comparisonType, setComparisonType] = useState(null);
    const [aiTask, setAiTask] = useState(defaultAiTask);
    const [comparisonItemUniqueId, setComparisonItemUniqueId] = useState(null);
    const [comparisonItemName, setComparisonItemName] = useState(null);
    const [userChatPlaceHolderText, setUserChatPlaceHolderText] = useState(defaultAiTask == "sr-summary" ? "AI Writing Assistant. Add your custom prompt here" : "Ask the AI Assistant");
    const [loadingSpinnerMessage, setLoadingSpinnerMessage] = useState(defaultAiTask == "sr-summary" ? "Analyzing..." : "Researching...");
    const [multiItemDataArray, setMultiItemDataArray] = useState(multiItemData);
    const [showRedLineComparisonPopup, setShowRedLineComparisonPopup] = useState(false);

    const [redLineOriginalUniqueId, setRedLineOriginalUniqueId] = useState(null);
    const [redLineOriginalMediaPath, setRedLineOriginalMediaPath] = useState(null);
    const [redLineOriginalTranslationMediaPath, setRedLineOriginalTranslationMediaPath] = useState(null);
    const [redLineOriginalTitle, setRedLineOriginalTitle] = useState(null);
    const [redLineComparisonUniqueId, setRedLineComparisonUniqueId] = useState(null);
    const [redLineComparisonMediaPath, setRedLineComparisonMediaPath] = useState(null);
    const [redLineComparisonTranslationMediaPath, setRedLineComparisonTranslationMediaPath] = useState(null);
    const [redLineComparisonTitle, setRedLineComparisonTitle] = useState(null);

    let showRegulatoryAITools = (systematicReviewType && systematicReviewType.startsWith("authorities-news"))
        || entityType === "compliance-regulatorydocuments"
        || entityType === "compliance-standards";

    let aiAPIUrl = chatApiUrl || ("/umbraco/api/DocumentAnalysis/Chat?entityType=" + entityType + "&entityId=" + entityId);
    let aiSrReportAPIUrl = "/umbraco/api/AI/SRReport?";
    let multiItemAnalysisAPIUrl = multiItemChatApiUrl || ("/umbraco/api/DocumentAnalysis/MultiItemChat?entityType=" + entityType);
    //let aiAPIUrl = "http://localhost:3000/stream/?";
    let reader = null;
    let highlightFields_FUNC = window.highlightFields; // This is the vanilla js preexisting function to do the highlight
    let setSuggestionFieldsValuesFromReactAIChat_FUNC = window.setSuggestionFieldsValuesFromReactAIChat; // This is the vanilla js preexisting function to set the suggestions
    let chatHistory = { "ChatHistory": aiChatHistory };
    const textareaRef = useRef(null);
    const sendButtonRef = useRef(null);
    const chatMessagesRef = useRef(null);
    const minAIChatTextareaHeight = '40px';
    var maxAIChatTextareaHeight = 300;
    let aiResultDivScrolltoBottom = useRef(true);
    let aiResultDivLastScrollTop = 0;
    let globalMessageId = useRef(1);

    const srAiReportRequestModes = { Chat: "0", Report1: "1" , Report2: "2" , Report3: "3" }; // Variables for distinguishing Sr Ai Report user chat message vs report request
    const srAiReportRequestMode = useRef(srAiReportRequestModes.Chat);
    const taskMaxHeights = {
        "sr-summary": "100%",
        "sr-summary-newsletter": "100%"
    };
    const entityTypeMaxHeights = {
        "compliance-regulatorydocuments": "max(380px, calc(76vh - 400px))",
        "compliance-standards": "max(380px, calc(76vh - 400px))",
        "reference-manager": "max(380px, calc(76vh - 400px))",
        "global-databases": "max(380px, calc(76vh - 400px))",
    };
    // Disable ai content auto scroll on mousescroll
    useEffect(() => {
        const chatContainer = chatMessagesRef.current;
    
        if (!chatContainer) return;

        const handleScroll = (event) => {
            aiResultDivScrolltoBottom.current = false;
        };

        // Add event listeners
        chatContainer.addEventListener('wheel', handleScroll);
        chatContainer.addEventListener('DOMMouseScroll', handleScroll); // For Firefox

        return () => {
            chatContainer.removeEventListener('wheel', handleScroll);
            chatContainer.removeEventListener('DOMMouseScroll', handleScroll);
        };
      }, []);

    // Resize textarea on user input
    useEffect(() => {
        if (textareaRef.current) {
            // Reset height to auto to get correct scrollHeight
            textareaRef.current.style.height = 'auto';
      
            // Calculate new height
            const currentHeight = textareaRef.current.scrollHeight;
            const newHeight = Math.min(currentHeight, maxAIChatTextareaHeight);
      
            // Apply the new height
            textareaRef.current.style.height = minAIChatTextareaHeight;
            textareaRef.current.style.height = `${newHeight}px`;
        }

        // Enable/disable send button
        if (sendButtonRef.current) {
            if (userText === "") {
                sendButtonRef.current.classList.remove("enabled");
            } else if (!sendButtonRef.current.classList.contains("enabled")) {
                sendButtonRef.current.classList.add("enabled");
            }
        }
      }, [userText]);

    // Scroll the chat div when new messages arrive
    useEffect(() => {

        scrollChatToBottom();

    }, [chatContent]);


    useEffect(() => {

        if (autoCallOverviewFunction) {
            //console.log("Calls AIOverview function")
            getAIOverview();
        }

    }, [autoCallOverviewFunction]);



    const getStreamSectionType = (section) => {

        switch (section) {
            case "AnalysisSections>Captures>Capture>Title": return "field";
            case "AnalysisSections>Captures>Capture>Value": return "value";
            case "AnalysisSections>Captures>Capture>Highlight": return "highlight";
            case "Summary": return "summary";
            default: return null;
        }
    }

    function scrollChatToBottom() {
        // scroll content to bottom?
    
        if (aiResultDivScrolltoBottom.current) {

            let content = chatMessagesRef.current;

            // Make sure not to scroll up when drawing tables for example
            if (aiResultDivLastScrollTop < content.scrollHeight) {
                content.scrollTop = content.scrollHeight;
            }
        }
        else {
            //console.log("scroll was disabled")
        }
    }

    // Don't add the Enter to the chat message if Shift isn't pressed
    const handleKeyDown = (event) => {
        if (!event.shiftKey && event.key === 'Enter')
            event.preventDefault();
    };

    // Check if user pressed Enter (without Shift) to submit
    const handleKeyUp = (event) => {
        if (event.key === 'Enter' && !event.shiftKey)
            sendChat({ aiTask: aiTask, comparisonItemUniqueId: comparisonItemUniqueId, comparisonItemName: comparisonItemName });
    };

    const generateAssistantMessageObject = (message = "") => {

        let id = globalMessageId.current++;
        //console.log("generateAssistantMessageObject going to use id " + id)
        return { "id": id, "role": "assistant", "content": message };
    }

    const generateCitationsObject = (citations = {}) => {

        let id = globalMessageId.current++;
        citations = JSON.parse(citations);

        return { "id": id, "role": "citations", "citations": citations };
    }

    const generateFeedbackObject = (relatedToId) => {

        let id = globalMessageId.current++;
        //console.log("generateFeedbackObject going to use id " + id)
        return { "id": id, "role": "feedback", "feedback": "", "relatedToId": relatedToId };
    }

    const generateUserMessageObject = (message = "") => {

        let id = globalMessageId.current++;
        //console.log("generateUserMessageObject going to use id " + id)
        return { "id": id, "role": "user", "content": message };
    }

    const UserMessage = ({ message }) => <div className="aichat-user-response"><div dangerouslySetInnerHTML={{ __html: message}}></div></div>;

    const AssistantMessage = ({ message }) => <div className="aichat-assistant-response"><div className="aichat-assistant-response-content" dangerouslySetInnerHTML={{ __html: message }} ></div></div>;

    const Citations = ({ citations = {} }) => {


        const showCitations = () => {

            let alreadyHasSummaryReference = false;

            if (citations == null)
                return <></>

            // Multi-PDF chat: each citation points at a specific document (ItemId / DocIndex).
            // Still render buttons when Coordinates are empty so the user can open that document.
            if (citations.IsMultiDocument && Array.isArray(citations.Items)) {
                return citations.Items.map((citationItem, index) => {
                    const coordinatesBlock = citationItem.Coordinates || [];
                    const hasCoordinates = coordinatesBlock.length > 0;
                    const documentTitle = citationItem.Document || "";
                    const title = hasCoordinates
                        ? documentTitle
                        : (documentTitle ? documentTitle + " — " : "") + "Location highlights are not available for this citation. Click to open the document.";
                    return (
                        <span
                            key={index}
                            title={title}
                            onClick={() => highlightMultiDocumentCitation(citationItem, coordinatesBlock, index + 1)}
                            className={'custom-question-citation' + (hasCoordinates ? '' : ' custom-question-citation--no-highlight')}
                        >
                            {index + 1}
                        </span>
                    );
                });
            }

            if (citations.Coordinates == null)
                return <></>

            return citations.Coordinates.map((coordinatesBlock, index) => {

                let isSummaryReference = false;

                if (coordinatesBlock.length == 0) {
                    if (alreadyHasSummaryReference)
                        return <></>

                    alreadyHasSummaryReference = true;
                    isSummaryReference = true;
                }

                return <span key={index} onClick={() => { if (!isSummaryReference) highlightPDFCitation(coordinatesBlock, index+1); }} className='custom-question-citation'>{index + 1}{isSummaryReference ? " (Abstract)" : ""}</span>
            });

        }

        return <>
            <div className="aichat-assistant-response-citations">
                {showCitations()}
            </div>
        </>



        //return <>
        //    <div className="aichat-assistant-response-citations">References:
        //        <br/>
        //        {citations.map(citation => {
        //            if(citation.Url == "")
        //                citationsDiv += "<span style=\"font-size: 10pt;\">[" + citation.Index + "] - " + citation.Title + "</span><br/>";
        //            else
        //                citationsDiv += "<span style=\"font-size: 10pt;\">[" + citation.Index + "] - <a href=\"" + citation.Url + "\" target=\"_blank\">" + citation.Title + "</a></span><br/>";
        //        })}
        //    </div>
        //</>
    }

    const Feedback = ({ id, relatedToId, selected = "" }) => {

        const [hovered, setHovered] = useState(null);

        return <>

            <div className="feedback-bar">
                <span
                    className="pointer feedback-icon"
                    onMouseEnter={() => setHovered('like')}
                    onMouseLeave={() => setHovered(null)}
                    onClick={() => changeFeedback(id, "like")}
                >
                    <i className={`fa-thumbs-up ${(selected == "like" || hovered == "like") && hovered != "dislike" ? 'fas' : 'far'}`}></i>
                </span>
                <span
                    className="pointer feedback-icon"
                    onMouseEnter={() => setHovered('dislike')}
                    onMouseLeave={() => setHovered(null)}
                    onClick={() => selected == "dislike" ? changeFeedback(id, "") : showFeedbackPopup(id)}
                >
                    <i className={`fa-thumbs-down ${(selected == "dislike" || hovered == "dislike") && hovered != "like" ? 'fas' : 'far'}`}></i>
                </span>
            </div>

        </>

    }

    // Change like/dislike for a specific feedback bar
    const changeFeedback = async (id, feedback) => {

        const authResponse = await fetch("/umbraco/api/ai/checkAuth");
        if (authResponse.status === 401) {
            document.dispatchEvent(new Event("api:unauthorized"));
            return false;
        }

        setChatContent(prev =>
            prev.map(item => {
                if (item.id == id) {
                    
                    if (item.feedback == feedback)
                        feedback = "";

                    return { ...item, feedback: feedback };
                }

                return item;
            })
        );

        if (feedback == "like")
            sendFeedback(id, feedback);

        return true;
    }


    const showFeedbackPopup = (id) => {
        
        setSelectedFeedbackId(id);
        setIsVisibleFeedbackPopup(true);

    }


    const clickedSendFeedback = async () => {

        if (feedbackMessage == "")
            return;

        let changed = await changeFeedback(selectedFeedbackId, "dislike");
        if (!changed)
            return;

        setFinishedFeedback(true);
        sendFeedback(selectedFeedbackId, "dislike");

    }


    const sendFeedback = (id, feedback) => {

        let feedbackObject = chatContent.find(item => item.id == id);
        let relatedMessage = chatContent.find(item => item.id == feedbackObject.relatedToId).content;


        let url = "/Umbraco/api/AI/AddFeedback";

        fetch(url, {
            method: "POST",
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                "UserFeedback": feedback,
                "UserExplanation": feedbackMessage,
                "AIAnswer": relatedMessage,
                "ChatHistory": {
                    "ChatHistory": aiChatHistory
                }
            })
        });

    }


    const closeFeedbackPopup = () => {

        setFeedbackMessage("");
        setIsVisibleFeedbackPopup(false);
        setFinishedFeedback(false);

    }


    const highlightPDFCitation = (coordinatesBlock, citationId = "") => {

        let appraisalType = ""; // It's not working with this currently

        let selectedCapture = {
            Captures: [
                {
                    Type: "",
                    Tokens: "",
                    Value: "Reference " + citationId,
                    Coordinates: coordinatesBlock
                }
            ]
                            
        };

        // Request came from appraisal card
        if (window.PDFDisplayRef)
            window.PDFDisplayRef[0].setHighlights(selectedCapture, appraisalType); // [0]: original, [1]: translation (if exists)

        // Request came from CM/RM/other react projects. Index 0 used to be hardcoded here,
        // assuming only one MedboardPdf would ever mount on a page — MedboardPdf.jsx now
        // un-registers itself on unmount, so the last entry is always the one actually still
        // mounted (and, in the ordinary case of exactly one PDF open, the only entry either way).
        else if (window.medboardPdfEmitter && window.medboardPdfEmitter.length > 0)
            window.medboardPdfEmitter[window.medboardPdfEmitter.length - 1].emit("sethighlights", {highlights: selectedCapture, appraisalType});

    }

    const clearPDFHighlights = () => {
        const emptyCapture = { Captures: [] };

        if (window.PDFDisplayRef)
            window.PDFDisplayRef[0].setHighlights(emptyCapture, "");
        else if (window.medboardPdfEmitter && window.medboardPdfEmitter.length > 0)
            window.medboardPdfEmitter[window.medboardPdfEmitter.length - 1].emit("sethighlights", { highlights: emptyCapture, appraisalType: "" });
    }

    // Multi-PDF citations: open the cited document on the left (same as list click), then highlight.
    // When the PDF viewer remounts after a document switch, poll briefly for the emitter.
    const highlightMultiDocumentCitation = (citationItem, coordinatesBlock, citationId = "") => {

        const itemId = citationItem?.ItemId;
        const targetItem = multiItemDataArray.find(i => String(i.Id) === String(itemId));
        const hasCoordinates = Array.isArray(coordinatesBlock) && coordinatesBlock.length > 0;

        const applyHighlightWhenReady = (attemptsLeft = 24) => {
            if (!hasCoordinates) {
                clearPDFHighlights();
                return;
            }

            const hasEmitter = window.medboardPdfEmitter && window.medboardPdfEmitter.length > 0;
            const hasPdfDisplay = window.PDFDisplayRef && window.PDFDisplayRef.length > 0;

            if (hasEmitter || hasPdfDisplay) {
                highlightPDFCitation(coordinatesBlock, citationId);
                return;
            }

            if (attemptsLeft > 0) {
                setTimeout(() => applyHighlightWhenReady(attemptsLeft - 1), 250);
            }
        };

        if (!targetItem || !targetItem.HasPDF || typeof callbackMultiItemDataSelectPDF !== "function") {
            if (hasCoordinates)
                highlightPDFCitation(coordinatesBlock, citationId);
            else
                clearPDFHighlights();
            return;
        }

        const selectedItem = multiItemDataArray.find(i => i.IsSelected);
        const needsDocumentSwitch = !selectedItem || String(selectedItem.Id) !== String(targetItem.Id);

        if (needsDocumentSwitch) {
            // Drop previous document's highlights immediately — PdfLoader keeps the same
            // MedboardPdf instance across URL changes, so highlight state would otherwise linger.
            clearPDFHighlights();
            changeMultiItemDataSelectPDF(targetItem);
            // Allow the left-pane PdfLoader time to swap documents before highlighting
            setTimeout(() => applyHighlightWhenReady(), 600);
            return;
        }

        if (hasCoordinates)
            highlightPDFCitation(coordinatesBlock, citationId);
        else
            clearPDFHighlights();
    }


    const formatedChatContent = (content) => {

        return content.map((message, index) => {

            let safeMessage = "";
            let citations = [];
            let feedback = "";
            let relatedToId = null;
            let id = null;

            if (message.role == "user") {
                safeMessage = DOMPurify.sanitize(marked(message.content));
                return <UserMessage key={index} message={safeMessage} />
            }

            if (message.role == "assistant") {
                safeMessage = DOMPurify.sanitize(marked(message.content));
                return <AssistantMessage key={index} message={safeMessage} />
            }

            if (message.role == "citations") {
                citations = message.citations;

                return <Citations key={index} citations={citations} />;
                // This is to be done when react ai chat replaces ai assistant
                //return <Citations citations={citations} />
            }

            if (message.role == "feedback") {
                feedback = message.feedback;
                relatedToId = message.relatedToId;
                id = message.id;
                return <Feedback key={index} id={id} relatedToId={relatedToId} selected={feedback} />
            }

            return null;

        });

    }


    const processFieldValues = (fieldsValues) => {

        return fieldsValues.map(item => ({ "Type": item.field, "Value": item.value, "Tokens": item.tokens }));
    }


    const openComparisonItemSearch = (type) => {

        setComparisonType(type);
        setShowComparisonItemSearchPopup(true);

    }


    const closeComparisonItemSearch = () => {
        setShowComparisonItemSearchPopup(false);
    }


    const selectComparisonItem = (comparisonUniqueId, itemName, mediaPath = "", mbri = "", translationPath = "") => {

        
        if (comparisonType == "redline") {
            { console.log(AppContext?.currentItem) }
            // Is it a redline comparison coming from systematic reviews AI?
            if (entityType == "systematic-reviews") {
                setRedLineOriginalUniqueId(uniqueId);
                setRedLineOriginalMediaPath(filePath);
                setRedLineOriginalTranslationMediaPath(translationMediaPath);
                setRedLineOriginalTitle(documentTitle);
                setRedLineComparisonUniqueId(comparisonUniqueId);
                setRedLineComparisonMediaPath(mediaPath);
                setRedLineComparisonTranslationMediaPath(translationPath);
                setRedLineComparisonTitle(itemName);
                closeComparisonItemSearch();
                setShowRedLineComparisonPopup(true);
                return;
            }

            // Is it a redline comparison coming from CM?
            setRedLineOriginalUniqueId(AppContext?.currentItem?.UniqueId); 
            if (AppContext?.currentItem?.MediaPath != null && AppContext?.currentItem?.MediaPath == "") {
                setRedLineOriginalMediaPath("/Umbraco/Api/SurveilanceAppraisal/LoadPdf?surveillanceItemId=" + AppContext?.currentItem?.MedboardResourceId);
            }
            else {
                setRedLineOriginalMediaPath(AppContext?.currentItem?.MediaPath);
            }
            setRedLineOriginalTitle(AppContext?.currentItem?.Name);
            setRedLineOriginalTranslationMediaPath(AppContext?.currentItem?.TranslationMediaPath);
            setRedLineComparisonUniqueId(comparisonUniqueId);
            setRedLineComparisonMediaPath(mediaPath);
            setRedLineComparisonTranslationMediaPath(translationMediaPath);
            setRedLineComparisonTitle(itemName);
            closeComparisonItemSearch();
            setShowRedLineComparisonPopup(true);
            return;
        }



        setAiTask(comparisonType);
        setComparisonItemUniqueId(comparisonUniqueId);
        setComparisonItemName(itemName);

        if(comparisonType == "comparison")
            sendChat({ aiTask: "comparison", comparisonItemUniqueId: comparisonUniqueId, comparisonItemName: itemName });


        closeComparisonItemSearch();

    }


    const clearComparisonItem = () => {

        setAiTask("question");
        setComparisonItemUniqueId(null);
        setComparisonItemName(null);

    }



    
    // Tries to process all jsons in the data: {json} chunk. If it fails then the data: {json} is incomplete and the chunk is marked as incomplete
    const isChunkIncomplete = (chunk) => {

        const regex = /^data:.*$/gm;
        let dataArray = chunk.match(regex);
        let error = false;

        try {
            dataArray.map(data => {
                data = data.replace(/^data: /, '');
                data = JSON.parse(data);
            });
        }
        catch (ex) {

            // Couldn't find valid data but is it because there is an error message in the response itself? If so assume chunk is complete but the response is an error message
            let regex2 = /\"error\":.*$/gm;
            let foundError = chunk.match(regex2);
            if (foundError)
                error = false;
            else
                error = true;
        }

        return error;
    }



    const isGetAIOverviewAvailable = useRef(true); // This is because of possible multiple requests while checking user authentication

    const getAIOverview = async () => {

        let currentSection;
        let currentSectionValue = "";
        let firstChunk = true;
        let isField = false;
        let isValue = false;
        let isHighlight = false;
        let isSummary = false;
        let lastField = "";
        let lastValue = "";
        let lastHighlight = "";
        let lastFieldNameForHighlights = "";
        let fieldsValues = [];
        let allPICOFieldsDone = false;
        let startedPICOFields = false;
        let aiTask = "overview";


        if (AITypeWriterIsTyping)
            return;


        
        if (isGetAIOverviewAvailable.current)
            isGetAIOverviewAvailable.current = false;
        else
            return;

        setAITypeWriterIsTyping(true);
        setShowAIOverviewButton(false);
        setIsLoading(true);

        const authResponse = await fetch("/umbraco/api/ai/checkAuth");

        isGetAIOverviewAvailable.current = true;

        if (authResponse.status === 401) {
            setShowAIOverviewButton(true);
            setAITypeWriterIsTyping(false);
            setIsLoading(false);
            document.dispatchEvent(new Event("api:unauthorized"));
            return;
        }



        setShowError(false);
        setShowCustomError(false);
        setIsLoading(true);
        setShowAIOverviewButton(false);
        setShowTemplateQuestions(false);


        let apiURL = aiAPIUrl + "&sessionId=" + sessionId + "&aiTask=" + aiTask;

        fetch(apiURL, {
                method: "POST",
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(chatHistory)
            })
        // Retrieve its body as ReadableStream
        .then((response) => {
            const reader = response.body.getReader();
            const decoder = new TextDecoder();
            let currentText = "";
            let chunk = "";
            let lastIncompleteChunk = "";

            // read() returns a promise that resolves when a value has been received
            reader.read().then(function pump({ done, value }) {

                try {

                    if (done) {

                        // Do highlighting for systematic reviews

                        if (entityType == "systematic-reviews") {
                            let processedFieldsValues = processFieldValues(fieldsValues);

                            // Call old vanilla js field highlighter
                            highlightFields_FUNC(processedFieldsValues);

                            // Call old vanilla js for storing the ai suggestions
                            setSuggestionFieldsValuesFromReactAIChat_FUNC(processedFieldsValues);
                        }
                        

                        // Activate user chat
                        setShowUserChatBox(true);
                        setAITypeWriterIsTyping(false);
                        return;
                    }

                    
                    chunk = decoder.decode(value, { stream: true });

                    if (lastIncompleteChunk != "") {
                        chunk = lastIncompleteChunk + chunk;
                        lastIncompleteChunk = "";
                    }

                    if (isChunkIncomplete(chunk)) {
                        lastIncompleteChunk = chunk;
                        return reader.read().then(pump);
                    }


                    // One chunk might have multiple data objects
                    const regex = /^data:.*$/gm;
                    const dataArray = chunk.match(regex);

                    let response = "";
                    let error = "";


                    // merge all the response data objects of the chunk in one string
                    dataArray.map(data => {

                        data = data.replace(/^data: /, '');
                        data = JSON.parse(data);
                        
                        if (data.sessionId && data.sessionId != "")
                            setSessionId(data.sessionId);

                        if (data.success) {

                            if (data.responseEvent == "START") {
                                //console.log("START OF " + currentSection);
                                currentSection = data.section;

                                isField = false;
                                isValue = false;
                                isHighlight = false;
                                isSummary = false;

                                if (getStreamSectionType(currentSection) == "field") isField = true;
                                if (getStreamSectionType(currentSection) == "value") isValue = true;
                                if (getStreamSectionType(currentSection) == "highlight") isHighlight = true;
                                if (getStreamSectionType(currentSection) == "summary") isSummary = true;

                                if (isField) {
                                    if (isClinicalLiterature && !allPICOFieldsDone)
                                        if (!startedPICOFields) {
                                            startedPICOFields = true;
                                            response += "<ul><li><b>";  // for starting PICO fields
                                        }
                                        else
                                            response += "<li><b>";  // for remaining PICO fields
                                    else
                                        response += "<br/><b>"; // For normal non-PICO fields
                                }

                                if (isSummary) {
                                    response += "<br/><br/><b>Summary:</b><br/>";
                                }

                            }
                            else if (data.responseEvent == "END") {
                                //console.log("FINAL VALUE: " + currentSectionValue);
                                //console.log("END OF " + currentSection);
                                currentSection = null;
                                currentSectionValue = "";

                                if (isField) {

                                    let customFieldName = lastField;

                                    if (isClinicalLiterature) {

                                        switch (lastField) {
                                            case "Population": customFieldName = "Population (P)"; break;
                                            case "Intervention": customFieldName = "Intervention (I)"; break;
                                            case "Comparison": customFieldName = "Comparison (C)"; break;
                                            case "Outcome": customFieldName = "Outcome (O)"; break;
                                            case "NumberPatients": customFieldName = "Number of Patients"; break;
                                            case "NumberOfPatients": customFieldName = "Number of Patients"; break;
                                            default: break;
                                        }

                                    }
                                    response += customFieldName + ":</b> ";
                                }

                                if (isValue) {

                                    if (isClinicalLiterature && !allPICOFieldsDone) {
                                        response += "</li>";
                                        if (lastField == "Outcome") {
                                            response += "</ul>";
                                            allPICOFieldsDone = true;
                                        }
                                    }

                                    // This stores the values of the fields to be used on the auto-fill
                                    fieldsValues.push({ field: lastField, value: lastValue });
                                    lastFieldNameForHighlights = lastField;
                                    lastField = "";
                                    lastValue = "";
                                }

                                // Is it the end of a Highlight section? Add the tokens to the correct field
                                if (isHighlight) {
                                    fieldsValues = fieldsValues.map(item => {
                                        if (item.field == lastFieldNameForHighlights) {
                                            let tokens = lastHighlight.split("|").map(item => item.trim());
                                            return { ...item, tokens };
                                        }

                                        return item;
                                    });

                                    // reset highlight text
                                    lastHighlight = "";

                                    // reset related field name for next highlight section
                                    lastFieldNameForHighlights = "";

                                    //console.log("FINAL FIELDVALUES ARRAY", fieldsValues);
                                }

                            }
                            else {

                                let text = data.response;

                                if(isValue || isSummary)
                                    response += text;

                                if (isField)
                                    lastField += text;
                                if (isValue)
                                    lastValue += text;
                                if (isHighlight)
                                    lastHighlight += text;

                            }

                        }

                        if (data.error) {
                            error = data.error;
                        }

                    });



                    if (error != "") {
                        setAITypeWriterIsTyping(false);
                        setIsLoading(false);
                        setCustomErrorMessage(error);
                        setShowCustomError(true);

                        setAITypeWriterIsTyping(false);

                        reader.cancel();
                        return;
                    }
                    else {

                        let initialText = "";

                        if (firstChunk && response != "") {
                            firstChunk = false;
                            // Remove loading icon
                            setIsLoading(false);

                            if (isClinicalLiterature)
                                initialText = "<b>PICO Values:</b><br/>";

                            // Add the new assistant block to the chatContent
                            let newAssistantMessage = generateAssistantMessageObject();

                            setChatContent(prev => [...prev, newAssistantMessage]);
                        }

                        if (response != "") {
                            currentText += initialText + response;

                            let formatted = marked(currentText);

                            // Update the last assistant response in the chatContent
                            setChatContent(prev => {
                                const updated = [...prev];
                                const last = updated[updated.length - 1];
                                updated[updated.length - 1] = { ...last, content: formatted };
                                return updated;
                            });
                        }

                    }

                    
                    // Read some more, and call this function again
                    return reader.read().then(pump);


                }
                catch (ex) {
                    setIsLoading(false);
                    setShowError(true);

                    console.log("EXCEPTION ex: " + ex);

                    setAITypeWriterIsTyping(false);
                    reader.cancel(); // Abort the request

                    logFrontendError("AI Overview Process Error", ex);

                    return;
                }

            });
        })
        .catch((err) => {
            setIsLoading(false);
            setShowError(true);

            console.log("EXCEPTION err: " + err);

            setAITypeWriterIsTyping(false);

            logFrontendError("AI Overview Fetch Error", err);
        });


    }










    // This is used by the appraisal card to call the AIOverview from the article analysis button
    window.getAIOverview_FUNC = getAIOverview;



    const getSRSummary = (srId, summaryType) => {

        let currentTask = summaryType == 4 ? "sr-summary-newsletter" : "sr-summary";
        setAiTask(currentTask);
        srAiReportRequestMode.current = summaryType;

        sendChat({ aiTask: currentTask, summarySystematicReviewId: srId });

        

        //setShowUserChatBox(true);
        //setUserText("");
        //setShowError(false);
        //setShowCustomError(false);
        //setIsLoading(true);
    }

    // This is used by the SR page to call the SR Summary from the export menu/popup
    if(defaultAiTask == "sr-summary")
        window.getSRSummary_FUNC = getSRSummary;






    const isSendChatAvailable = useRef(true); // This is because of possible multiple requests while checking user authentication

    const sendChat = async ({ aiTask = "question", comparisonItemUniqueId = null, comparisonItemName = null, summarySystematicReviewId = null }) => {

        if (AITypeWriterIsTyping)
            return;


        if (isSendChatAvailable.current)
            isSendChatAvailable.current = false;
        else
            return;



        const authResponse = await fetch("/umbraco/api/ai/checkAuth");

        isSendChatAvailable.current = true;

        if (authResponse.status === 401) {
            document.dispatchEvent(new Event("api:unauthorized"));
            return;
        }


        let addUserMessage = true;
        let newUserChatMessage = userText;
        let currentRequestAiReportRequestMode = srAiReportRequestMode.current;

        srAiReportRequestMode.current = srAiReportRequestModes.Chat; // Back to chat mode


        if (aiTask == "requirements") {
            newUserChatMessage = "AI Tool: Extract Requirements";
            //setShowRequirementsButton(false);
            setShowRequirementsButtonDisabled(true);
        }


        if (aiTask == "comparison") {
            newUserChatMessage = "AI Tool: GAP Analysis -> '\"*" + comparisonItemName + "*\"'";
            //setShowComparisonButton(false);
            //setShowVersionComparisonButtonDisabled(true);
        }


        if (aiTask == "sr-summary" || aiTask == "sr-summary-newsletter") {

            switch (currentRequestAiReportRequestMode) {
                case 1: newUserChatMessage = "Summarize this systematic review type 1"; break;
                case 2: newUserChatMessage = "Summarize this systematic review type 2"; break;
                case 3: newUserChatMessage = "Generate AI Draft Report"; break;
                case 4: newUserChatMessage = "Generate the newsletter"; break;
                default: break;
            }

            //newUserChatMessage = "Summarize this systematic review";
            //addUserMessage = false;
            //setShowComparisonButton(false);
            //setShowVersionComparisonButtonDisabled(true);
        }


        
        if (newUserChatMessage == "")
            return;

        if (aiTask == "sr-summary" || aiTask == "sr-summary-newsletter") {
            
            if (currentRequestAiReportRequestMode == 3 || currentRequestAiReportRequestMode == 4) {

                if (window.summaryAIReportInput && window.summaryAIReportInput != "")
                    newUserChatMessage += " taking in account the additional instructions: " + window.summaryAIReportInput;

                setShowUserChatBox(false);
            }

            // Send the signal back to the original legacy page to hide buttons
            if (currentRequestAiReportRequestMode == 0)
                if (typeof window.AISummaryReportPageHideLayout_FUNC === "function")
                    window.AISummaryReportPageHideLayout_FUNC();

            // disable new chat button from outside the react AIChat
            document.getElementById("ai-new-report-button").style.display = "none";
        }

        setAITypeWriterIsTyping(true);
        aiResultDivScrolltoBottom.current = true;


        let message = generateUserMessageObject(newUserChatMessage);
        let firstChunk = true;
        let relatedToId = null;

        if(textareaRef.current)
            textareaRef.current.style.height = minAIChatTextareaHeight;



        // Add user message to AIChat history
        let newAiChatHistory = [...aiChatHistory, message];
        let local_chatHistory = chatHistory;
        local_chatHistory.ChatHistory = newAiChatHistory;
        setAiChatHistory(prev => newAiChatHistory);

        // Format the user message display to have line breaks
        newUserChatMessage = newUserChatMessage.replaceAll("\n", "<br/>");


        // For normal chat do this, for sr-summary case don't write anything for user input
        if (addUserMessage) {

            // Add user message to chat display and clear input

            let newChatContentUserMessage = generateUserMessageObject(marked(newUserChatMessage));
            setChatContent(prev => [...prev, newChatContentUserMessage]);

        }


        

        setUserText("");
        setShowError(false);
        setShowCustomError(false);
        setShowAIOverviewButton(false);
        setIsLoading(true);

        let apiURL = aiAPIUrl + "&sessionId=" + sessionId + "&aiTask=" + aiTask;


        if (aiTask == "comparison" || aiTask == "file-included")
            apiURL += "&comparisonUniqueId=" + comparisonItemUniqueId;


        if (aiTask == "sr-summary" || aiTask == "sr-summary-newsletter") {

            let aiReportSelectedQuestionIds = "";
            let windowObj = window.aiReportSelectedQuestionIds; // This comes from the report popup page

            // Try to get the current questions if it's not initialized yet
            if (!windowObj) {
                window.aiReportSelectedQuestionIds = window.getAiReportSelectedQuestionIds();
                windowObj = window.aiReportSelectedQuestionIds;
            }

            if(Array.isArray(windowObj))
                aiReportSelectedQuestionIds = windowObj.join(",");


            if (summarySystematicReviewId == null) {
                summarySystematicReviewId = window.summarySystematicReviewId;
            }



            let filters = window.getAllFiltersIncludingSearchAndDate_FUNC();

            if (aiTask == "sr-summary") {
                apiURL = aiSrReportAPIUrl + "&srId=" + summarySystematicReviewId + "&customQuestionIds=" + aiReportSelectedQuestionIds + "&mode=" + currentRequestAiReportRequestMode + "&sessionId=" + sessionId;
                apiURL += filters;
            }

            if (aiTask == "sr-summary-newsletter") {
                apiURL = aiSrReportAPIUrl + "&srId=" + summarySystematicReviewId + "&customQuestionIds=" + aiReportSelectedQuestionIds + "&mode=" + currentRequestAiReportRequestMode + "&sessionId=" + sessionId;
                apiURL += filters;
            }

            window.switchAiReportTab_FUNC('ai-chat-tab');

        }


        if (aiTask == "multi-item-analysis") {

            let multiItemDataIds = multiItemDataArray.map(item => item.Id);

            apiURL = multiItemAnalysisAPIUrl + "&items=" + multiItemDataIds + "&sessionId=" + sessionId;
        }

             
        fetch(apiURL, {
                method: "POST",
                headers: {
                  'Content-Type': 'application/json',
                },
                body: JSON.stringify(local_chatHistory)
            })
        // Retrieve its body as ReadableStream
        .then((response) => {
            const reader = response.body.getReader();
            const decoder = new TextDecoder();
            let currentText = "";
            let citations = "";
            let chunk = "";
            let lastIncompleteChunk = "";

            
            // read() returns a promise that resolves when a value has been received
            reader.read().then(function pump({ done, value }) {

                try {

                    if (done) {
                        // Do something with last chunk of data then exit reader
                        //console.log("final chunk")
                        //console.log(value)

                        let newMessage = generateAssistantMessageObject(currentText);
                        setAiChatHistory(prev => [...prev, newMessage]);

                        setAITypeWriterIsTyping(false);
                        try {
                            if (citations != null && citations != "") {
                                let newAssistantCitations = generateCitationsObject(citations);
                                setChatContent(prev => [...prev, newAssistantCitations]);
                            }
                        }
                        catch (ex) {

                            console.log("Citation Error: ", ex)
                        }
                        // Add like/dislike buttons
                        let newFeedbackBar = generateFeedbackObject(relatedToId);
                        setChatContent(prev => [...prev, newFeedbackBar]);


                        if (aiTask == "sr-summary" || aiTask == "sr-summary-newsletter") {
                            // reactivate the new chat button
                            document.getElementById("ai-new-report-button").style.display = "block";
                        }

                        return;
                    }

                    chunk = decoder.decode(value, { stream: true });

                    if (lastIncompleteChunk != "") {
                        chunk = lastIncompleteChunk + chunk;
                        lastIncompleteChunk = "";
                    }

                    if (isChunkIncomplete(chunk)) {
                        lastIncompleteChunk = chunk;
                        return reader.read().then(pump);
                    }

                    // One chunk might have multiple data objects
                    const regex = /^data:.*$/gm;
                    let dataArray = chunk.match(regex);

                    let response = "";
                    let error = "";

                    // merge all the response data objects of the chunk in one string and, if present, get the citations (all citations are together in only 1 data object)
                    dataArray.map(data => {

                        data = data.replace(/^data: /, '');
                        data = JSON.parse(data);

                        if (data.sessionId && data.sessionId != "")
                            setSessionId(data.sessionId);

                        if (data.success) {

                            if (data.response)
                                response += data.response;

                            if (data.citations)
                                citations += data.citations;
                        }

                        if (data.error) {
                            error = data.error;
                        }

                    });

                    if (error != "") {
                        setAITypeWriterIsTyping(false);
                        setIsLoading(false);
                        setCustomErrorMessage(error);
                        setShowCustomError(true);

                        reader.cancel();
                        return;
                    }
                    else {

                        if (firstChunk && response != "") {
                            firstChunk = false;

                            // Remove loading icon
                            setIsLoading(false);

                            // Add the new assistant block to the chatContent
                            let newAssistantMessage = generateAssistantMessageObject();
                            setChatContent(prev => [...prev, newAssistantMessage]);
                            relatedToId = newAssistantMessage.id; // This is for the feedback component
                        }

                        if (response != "") {
                            let token = response;
                            currentText += token;

                            let formatted = marked(currentText);

                            // Update the last assistant response in the chatContent
                            // This forces the correct update of the state since it's change of a property inside an object inside an array
                            // it wasn't triggering the re-render correctly previously.
                            setChatContent(prev => {
                                const updated = [...prev];
                                const last = updated[updated.length - 1];
                                updated[updated.length - 1] = { ...last, content: formatted };
                                return updated;
                            });
                        }

                        

                    }

                    
                    // Read some more, and call this function again
                    return reader.read().then(pump);


                }
                catch (ex) {

                    console.log("Err#6", ex)
                    setIsLoading(false);
                    setShowError(true);
                    setAITypeWriterIsTyping(false);
                    reader.cancel(); // Abort the request

                    logFrontendError("AI Chat Process Error", ex);

                    return;
                }

            });
        })
        .catch((err) => {
            setIsLoading(false);
            setShowError(true);
            console.log("Err#5", err)
            setAITypeWriterIsTyping(false);

            logFrontendError("AI Chat Fetch Error", err);

        });




    }










    
    const logFrontendError = (eventName, ex) => {

        try {
            let errorURL = `/umbraco/api/EventLogging/Event?eventName=${encodeURIComponent(eventName)}`;

            let extraInfo = { error: ex.message };

            fetch(errorURL, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json"
                },
                body: JSON.stringify(extraInfo)
            });

        }
        catch (ex) { }
    }






    const toggleOptionsMenu = (e) => {

        e.stopPropagation();

        const rect = e.currentTarget.getBoundingClientRect();
        const position = {
            top: rect.top + 20,
            left: rect.left,
        };

        setOptionsMenuPosition({ top: position.top, left: position.left });
        setIsShowingOptionsMenu(!isShowingOptionsMenu);

    }


    const mainComponentClickEvent = () => {

        setIsShowingOptionsMenu(false);

    }



    const changeMultiItemDataSelectPDF = (item) => {

        setMultiItemDataArray(multiItemDataArray.map(i => {

            if (i.Id == item.Id)
                i.IsSelected = true;
            else
                i.IsSelected = false;

            return i;

        }));

        callbackMultiItemDataSelectPDF(item.HasPDF);

    }


    return (<>


        <div id="ai-chat" onClick={mainComponentClickEvent} style={{flex: "1", height: "100%", display: "flex", flexDirection: "column",backgroundColor: "white", margin: "10px", padding: "10px", borderRadius: "15px"}}>


        
           

            {/* Section 1: Rendered when multiItemDataArray is empty or hasn't loaded yet */}
            {(!multiItemDataArray || multiItemDataArray.length === 0) && (
                <div style={{ display: "flex", flexDirection: "row", justifyContent: "start", background: "#f7fafc", padding: "15px 7px", borderRadius: "15px" }}>

                    <div id="" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 32%))", gap: "12px", width: "100%" }} >

                        {/* AI button for "AIOverview" */}
                        {showAIOverviewButton ? <>
                            <div style={{ textAlign: "center" }}>
                                <div
                                    onClick={!AITypeWriterIsTyping ? () => getAIOverview() : undefined}
                                    className={`ai-tool ${AITypeWriterIsTyping ? "disabled" : ""}`}
                                >
                                    <div className="tool-icon">
                                        <i className="fas fa-magic"></i>
                                    </div>
                                    <div className="tool-title">
                                        AI Overview
                                    </div>
                                    <div className="tool-arrow">
                                        <i className="fas fa-chevron-right"></i>
                                    </div>
                                </div>
                            </div>
                        </> : null}

                        {/* Redline Comparison Button */}
                        {showRegulatoryAITools && otherDocVersions && filePath && (
                            <div style={{ textAlign: "center" }}>
                                <div
                                    onClick={() => openComparisonItemSearch("redline")}
                                    className="ai-tool"
                                >
                                    <div className="tool-icon">
                                        <i className="fas fa-exchange-alt"></i>
                                    </div>
                                    <div className="tool-title">
                                        Redline Comparison
                                    </div>
                                    <div className="tool-arrow">
                                        <i className="fas fa-chevron-right"></i>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* GAP Analysis Button */}
                        {false && showRegulatoryAITools && showVersionComparisonButton && otherDocVersions && filePath && (
                            <div style={{ textAlign: "center" }}>
                                <div
                                    onClick={!AITypeWriterIsTyping ? () => openComparisonItemSearch("comparison") : undefined}
                                    className={`ai-tool ${AITypeWriterIsTyping ? "disabled" : ""}`}
                                >
                                    <div className="tool-icon">
                                        <i className="fas fa-chart-bar"></i>
                                    </div>
                                    <div className="tool-title">
                                        GAP Analysis
                                    </div>
                                    <div className="tool-arrow">
                                        <i className="fas fa-chevron-right"></i>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Requirements Extraction Button */}
                        {showRegulatoryAITools && showRequirementsButton && filePath && (
                            <div style={{ textAlign: "center" }}>
                                <div
                                    onClick={!AITypeWriterIsTyping ? () => sendChat({ aiTask: "requirements" }) : undefined}
                                    className={`ai-tool ${AITypeWriterIsTyping ? "disabled" : ""}`}
                                >
                                    <div className="tool-icon">
                                        <i className="fas fa-balance-scale"></i>
                                    </div>
                                    <div className="tool-title">
                                        Requirements Extraction
                                    </div>
                                    <div className="tool-arrow">
                                        <i className="fas fa-chevron-right"></i>
                                    </div>
                                </div>
                            </div>
                        )}

                    </div>
                </div>
            )}

            {/* Section 2: Rendered when multiItemDataArray has documents selected */}
            {multiItemDataArray?.length > 0 && (
                <div className="multiple-item-ai-chat-container">
                    <div className="multiple-item-ai-chat-header">
                        Selected Documents
                    </div>

                    <div className="multiple-item-ai-chat-list">
                        {multiItemDataArray.map((item, index) => (
                            <div key={index} className="multiple-item-ai-chat-chip">
                                {item.HasPDF ? (
                                    <span
                                        style={{ cursor: "pointer", fontWeight: item.IsSelected ? "bold" : "normal" }}
                                        title="View PDF"
                                        onClick={() => changeMultiItemDataSelectPDF(item)}
                                    >
                                        <i style={{ color: "red" }} className="far fa-file-pdf multiple-item-ai-chat-icon"></i> {item.Name}
                                    </span>
                                ) : (
                                    <span>
                                        <i className="far fa-file multiple-item-ai-chat-icon"></i> {item.Name}
                                    </span>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            )}

            <div
                ref={chatMessagesRef}
                id="ai-chat-messages"
                className={embeddedAIComponent ? "ai-chat-messages-embedded" : ""}
                style={{
                    flex: 1,
                    overflowY: "auto",
                    maxHeight: taskMaxHeights[aiTask] || entityTypeMaxHeights[entityType] || "max(200px, calc(76vh - 400px))"
                }}
            >
                {formatedChatContent(chatContent)}
            </div>

            
            {isLoading ? <>
                <div style={{ width: "100%" }}>
                    <LoadingSpinnerImage /> {loadingSpinnerMessage}
                </div>
            </> : null}




            {showTemplateQuestions ? <>

                <div>
                    Template questions
                </div>

            </> : null}

          




            {showError ? <>
                <div id="error">
                    <span><i className="fas fa-exclamation-triangle"></i> Our servers are busy. Please try again later...</span>
                </div>
            </> : null}

            {showCustomError ? <>
                <div id="custom-error">
                    <i className="fas fa-exclamation-triangle"></i> {customErrorMessage}
                </div>
            </> : null}




            {showUserChatBox ? <>
                <div style={{marginBottom: "15px"}}></div>

                <div id="mb-ai-user-chat-box" style={{display: "flex", flexDirection: "column", border: "1px solid #aaa", borderRadius: "15px", padding: "15px", boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)" }}>
                    <div style={{ flex: 1, display: "flex", flexDirection: "row" }} >
                        
                        <textarea
                            ref={textareaRef}
                            onKeyDown={handleKeyDown}
                            onKeyUp={handleKeyUp}
                            value={userText}
                            onChange={e => setUserText(e.target.value)}
                            style={{ flex: 1, width: "100%", overflow: "hidden", resize: "none", border: "0px", outline: "none", fontSize: "13px", minHeight: "30px" }}
                            placeholder={userChatPlaceHolderText}
                            aichat-request-chat="" />
                            
                        <div style={{ width: "40px", textAlign: "center", display: "flex", alignItems: "end", flexDirection: "column", justifyContent: "end" }}>
                            <div ref={sendButtonRef} id="sendButton" onClick={() => sendChat({ aiTask: aiTask, comparisonItemUniqueId: comparisonItemUniqueId, comparisonItemName: comparisonItemName })} style={{ cursor: "pointer", width: "35px", height: "35px", display: "inline", padding: "3px", borderRadius: "20px", border: "0px", backgroundColor: "#c6c6c6", display: "flex", justifyContent: "center", alignItems: "center", color: "white" }} >
                                <i className="fas fa-arrow-up" style={{ fontSize: "12pt" }}></i>
                            </div>
                        </div>
                    </div>


                </div>


                {showDisclaimer ? <>
                    <div id="disclaimer">
                        AI-generated. Check important info.
                    </div>
                </> : null}



            </> : null}


        </div>


        {isVisibleFeedbackPopup ? <>
            <div id="feedback-popup">

                <div id="feedback-window">
                    <div style={{ display: "flex", flexDirection: "row" }} >
                        <div style={{ width: "50%", textAlign: "left" }} ><h3>Feedback</h3></div>
                        <div style={{ width: "50%", textAlign: "right" }}><span id="feedback-close" onClick={closeFeedbackPopup} ><i className="fas fa-times-circle"></i></span></div>
                    </div>

                    {!finishedFeedback ? <>
                        <div id="feedback-content-div">
                            <textarea id="feedback-message" value={feedbackMessage} onChange={(e) => setFeedbackMessage(e.target.value)} placeholder="We appreciate your feedback. Please share any comments or suggestions that you have to help us improve."></textarea>

                            <div style={{ display: "flex", flexDirection: "row", justifyContent: "end", marginTop: "15px" }} >
                                <input id="feedback-button-cancel" type="button" onClick={closeFeedbackPopup} value="Cancel" />
                                <input id="feedback-button-send" type="button" onClick={clickedSendFeedback} value="Send feedback" />
                            </div>
                        </div>
                    </> : <>
                        <div id="feedback-thankyou-div">
                            <div style={{ textAlign: "center", fontSize: "12pt" }}>Thank you for the feedback!</div>
                            <div style={{ display: "flex", flexDirection: "row", justifyContent: "center", marginTop: "15px" }}>
                                <input id="feedback-button-close" type="button" onClick={closeFeedbackPopup} value="Close" />
                            </div>
                        </div>
                    </>}

                </div>

            </div>
        </> : null}



        {showComparisonItemSearchPopup ? <>

            <div id="comparison-filesearch-popup">
                <div>

                    <ComparisonItemSearch comparisonType={comparisonType} entityType={entityType} uniqueId={uniqueId} closePopupFunction={closeComparisonItemSearch} selectComparisonItemFunction={selectComparisonItem} systematicReviewType={systematicReviewType} filePath={filePath} />

                </div>
            </div>

        </> : null}



        {showRedLineComparisonPopup ? <>

            <div style={{
                position: "fixed",
                top: "0px",
                left: "0px",
                width: "100%",
                backgroundColor: "white",
                height: "100vh",
                padding: "15px",
                zIndex: "3",
            }}>
                <RegulatoryReviewsDocumentComparison
                    entityType={entityType}
                    originalUniqueId={redLineOriginalUniqueId}
                    originalMediaPath={redLineOriginalMediaPath}
                    originalTranslationMediaPath={redLineOriginalTranslationMediaPath}
                    originalTitle={redLineOriginalTitle}
                    comparisonUniqueId={redLineComparisonUniqueId}
                    comparisonMediaPath={redLineComparisonMediaPath}
                    comparisonTranslationMediaPath={redLineComparisonTranslationMediaPath}
                    comparisonTitle={redLineComparisonTitle}
                    closePopup={() => setShowRedLineComparisonPopup(false)} />
            </div>

        </> : null}


    </>);




};

export default AIChat;